import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from '@phosphor-icons/react';
import AdvanceDataTable from '@/components/core/advance-data-table/advance-data-table';
import { ConfirmationModal } from '@/components/core/confirmation-modal/confirmation-modal';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui-kit/select';
import { HRM_PERMISSIONS } from '@/constant/hr/permissions';
import { useHasPermission } from '@/hooks/use-has-permission';
import { useServerTable } from '@/hooks/use-server-table';
import { mergeWhere } from '@/lib/table-query';
import { orgWallClock } from '@/lib/utils/custom-date/custom-date';
import { createHolidayTableColumns } from '../../components/holiday-table-columns/holiday-table-columns';
import { HolidayFormDialog } from '../../components/holiday-form-dialog/holiday-form-dialog';
import { useGetHolidayCantons } from '../../hooks/use-holiday-lookups';
import { useDeleteHoliday, useGetHolidays } from '../../hooks/use-holidays';
import { DEFAULT_HOLIDAYS_ORDER } from '../../graphql/queries';
import { Holiday, HolidaysPagingResult } from '../../types/holiday.types';
import { PageHeader } from '@/components/core';

export const HolidayPage = () => {
  const { t } = useTranslation();
  const [year, setYear] = useState(() => orgWallClock(new Date()).year);
  const canCreate = useHasPermission(HRM_PERMISSIONS.HOLIDAY.CREATE);
  const canUpdate = useHasPermission(HRM_PERMISSIONS.HOLIDAY.UPDATE);
  const canDelete = useHasPermission(HRM_PERMISSIONS.HOLIDAY.DELETE);
  const canMarkCantonBased = useHasPermission(HRM_PERMISSIONS.HOLIDAY.MARK_CANTON_BASED);

  const { pagination, resetPage, order, columnWhere, tableProps } = useServerTable({
    pageSize: 15,
    defaultOrder: DEFAULT_HOLIDAYS_ORDER,
  });
  const [formState, setFormState] = useState<{ open: boolean; holiday: Holiday | null }>({
    open: false,
    holiday: null,
  });
  const [deleteState, setDeleteState] = useState<{ open: boolean; holiday: Holiday | null }>({
    open: false,
    holiday: null,
  });

  // `StartDate` is a date-only `DateTime` (the day at 00:00:00 UTC), inlined as a query literal (see
  // graphql-query-builder.ts) that needs a full ISO-8601 value. The year is [1 Jan, next 1 Jan).
  const filter = {
    StartDate: {
      $gte: `${year}-01-01T00:00:00.000Z`,
      $lt: `${year + 1}-01-01T00:00:00.000Z`,
    },
  };
  const { data, isLoading, error } = useGetHolidays({
    pageNo: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
    filter: mergeWhere(filter, columnWhere),
    order,
  });
  const result = (data as { Holidays?: HolidaysPagingResult })?.Holidays;
  const items = result?.items ?? [];
  const totalCount = result?.totalCount ?? 0;

  const { mutateAsync: deleteHoliday, isPending: isDeleting } = useDeleteHoliday();

  const handleDeleteConfirm = async () => {
    if (!deleteState.holiday) return;
    await deleteHoliday(deleteState.holiday.ItemId);
    setDeleteState({ open: false, holiday: null });
  };

  // Roles holding the permission see cantons; everyone else sees them only once a holiday on the page has
  // some, so a region that doesn't use cantons just shows names and dates. A canton filter keeps the
  // column, so it can still be cleared when nothing matches.
  const showCantons =
    canMarkCantonBased ||
    items.some((holiday) => holiday.IsRegional) ||
    tableProps.columnFilters.some((filter) => filter.id === 'CantonIds');
  const { data: cantons } = useGetHolidayCantons(showCantons);
  const cantonNames = useMemo(
    () => new Map((cantons ?? []).map((canton) => [canton.ItemId, canton.State])),
    [cantons]
  );
  const cantonOptions = useMemo(
    () => (cantons ?? []).map((canton) => ({ value: canton.ItemId, label: canton.State })),
    [cantons]
  );

  const getCantonLabel = (holiday: Holiday) => {
    if (!holiday.IsRegional || !holiday.CantonIds?.length) return t('NATIONWIDE');
    // Until the cantons have loaded there are no names to show.
    return (
      holiday.CantonIds.map((id) => cantonNames.get(id))
        .filter(Boolean)
        .join(', ') || '-'
    );
  };

  const columns = createHolidayTableColumns({
    t,
    canEdit: canUpdate,
    canDelete,
    showCantons,
    getCantonLabel,
    cantonOptions,
    onEdit: (holiday) => setFormState({ open: true, holiday }),
    onDelete: (holiday) => setDeleteState({ open: true, holiday }),
  });

  const yearOptions = [year - 1, year, year + 1];

  return (
    <div className="flex w-full flex-col gap-4">
      {/* The year picker, then the create button (icon-only on a phone). */}
      <PageHeader
        title={t('HOLIDAYS')}
        actions={
          canCreate
            ? [
                {
                  label: t('CREATE_HOLIDAY'),
                  icon: <Plus className="size-5" />,
                  onClick: () => setFormState({ open: true, holiday: null }),
                },
              ]
            : []
        }
      >
        <Select
          value={String(year)}
          onValueChange={(value) => {
            setYear(Number(value));
            resetPage();
          }}
        >
          <SelectTrigger className="w-24 sm:w-28 max-md:h-10" aria-label={t('YEAR')}>
            <SelectValue placeholder={t('YEAR')} />
          </SelectTrigger>
          <SelectContent>
            {yearOptions.map((option) => (
              <SelectItem key={option} value={String(option)}>
                {option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </PageHeader>

      <AdvanceDataTable
        {...tableProps}
        data={items}
        columns={columns}
        isLoading={isLoading}
        error={error instanceof Error ? error : null}
        pagination={{
          pageIndex: pagination.pageIndex,
          pageSize: pagination.pageSize,
          totalCount,
        }}
        isExpandRowContent={false}
        mobileView="cards"
      />

      <HolidayFormDialog
        open={formState.open}
        onOpenChange={(open) =>
          setFormState((prev) => ({ open, holiday: open ? prev.holiday : null }))
        }
        holiday={formState.holiday}
      />

      <ConfirmationModal
        open={deleteState.open}
        onOpenChange={(open) =>
          setDeleteState({ open, holiday: open ? deleteState.holiday : null })
        }
        title={t('DELETE_HOLIDAY')}
        description={t('ARE_YOU_SURE_WANT_DELETE')}
        confirmText={isDeleting ? t('DELETING') : t('DELETE')}
        preventAutoClose={isDeleting}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
};
